import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Package, FileText, User, Building2, CheckCircle, XCircle, Clock } from 'lucide-react';
import API_BASE_URL from "../config";
import bellSound from '../SoundBell/Soundbells.mp3';
import useNotificationPolling from '../utils/useNotificationPolling';


const STATUS_MAP = {
  '-1': { label: 'Rejected',  bg: 'from-red-500 to-red-600',        icon: XCircle,      body: 'Your request has been rejected.' },
  '1':  { label: 'Pending',   bg: 'from-amber-500 to-amber-600',    icon: Clock,        body: 'Your request is pending approval.' },
  '2':  { label: 'Approved',  bg: 'from-emerald-500 to-emerald-600',icon: CheckCircle,  body: 'Your request has been approved!' },
  '3':  { label: 'Issued',    bg: 'from-blue-500 to-blue-700',      icon: CheckCircle,  body: 'Your request has been issued!' },
};

export default function MRNotification() {
  const [alerts, setAlerts]               = useState([]);
  const [profileCache, setProfileCache]   = useState({});
  const [notifSettings, setNotifSettings] = useState({ notice_sound: true, notice_view: true });

  const prevDataRef      = useRef(null);
  const lastUpdatedRef   = useRef(null);
  const userInteracted   = useRef(false);

  const currentUser   = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser?.userId;

  // ── Fetch NotificationSettings once on mount ──
  useEffect(() => {
    if (!currentUserId) return;
    fetch(`${API_BASE_URL}api/NotificationSettings/user/${currentUserId}`)
      .then(r => r.json())
      .then(d => setNotifSettings(d))
      .catch(() => {});
  }, [currentUserId]);

  const isSoundOn = (s) => s === true || s === 1 || String(s).toLowerCase() === 'true';
  const isViewOn  = (s) => s === true || s === 1 || String(s).toLowerCase() === 'true';

  // ✅ Track any user interaction so audio is allowed by browser
  useEffect(() => {
    const handler = () => { userInteracted.current = true; };
    window.addEventListener('click',   handler);
    window.addEventListener('keydown', handler);
    window.addEventListener('scroll',  handler);
    return () => {
      window.removeEventListener('click',   handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('scroll',  handler);
    };
  }, []);

  // ✅ Safe playSound — blocked if notice_sound is false
  const playSound = useCallback(() => {
    if (!isSoundOn(notifSettings.notice_sound)) return;
    if (!userInteracted.current) return;
    try {
      const audio = new Audio(bellSound);
      audio.volume = 0.7;
      audio.play().catch(() => {});
    } catch (err) {}
  }, [notifSettings.notice_sound]);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) return;
    setProfileCache(prev => {
      if (prev[userId] !== undefined) return prev;
      return { ...prev, [userId]: null };
    });
    try {
      const res = await fetch(`${API_BASE_URL}api/Users/${userId}/profile`);
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await res.json();
          const pic  = data?.profileBase64 || null;
          setProfileCache(prev => ({ ...prev, [userId]: pic ? `data:image/jpeg;base64,${pic}` : null }));
        } else {
          const blob = await res.blob();
          const url  = URL.createObjectURL(blob);
          setProfileCache(prev => ({ ...prev, [userId]: url }));
        }
      }
    } catch (err) {
      setProfileCache(prev => ({ ...prev, [userId]: null }));
    }
  }, []);

  const addAlert = useCallback((alert) => {
    // ── Blocked if notice_view is false ──
    if (!isViewOn(notifSettings.notice_view)) {
      playSound();
      return;
    }
    const alertWithId = { ...alert, uid: `${alert.mrId}-${alert.type}-${Date.now()}` };
    setAlerts(prev => [...prev, alertWithId]);
    if (alert.userId) fetchProfile(alert.userId);
    playSound();
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.uid !== alertWithId.uid));
    }, 8000);
  }, [fetchProfile, playSound, notifSettings.notice_view]);

  const processData = useCallback((arr, isFirstLoad) => {
    if (isFirstLoad) {
      const snapshot = {};
      arr.forEach(mr => {
        snapshot[mr.id] = {
          status:      mr.status,
          updatedDate: mr.updatedDate,
          checkBy:     mr.checkBy,
        };
      });
      prevDataRef.current    = snapshot;
      const dates            = arr.map(m => new Date(m.updatedDate || m.createdDate || 0));
      lastUpdatedRef.current = dates.length ? Math.max(...dates) : 0;
      return;
    }

    const prev        = prevDataRef.current || {};
    const newSnapshot = {};
    let latestDate    = lastUpdatedRef.current || 0;

    arr.forEach(mr => {
      const updDate = new Date(mr.updatedDate || mr.createdDate || 0).getTime();
      if (updDate > latestDate) latestDate = updDate;

      // ── NEW MR submitted by someone else ──
      if (!prev[mr.id]) {
        if (String(mr.requestedBy) !== String(currentUserId)) {
          addAlert({
            type:       'new',
            mrId:       mr.id,
            userId:     mr.requestedBy,
            title:      'New Item Request',
            name:       mr.requestedByName || 'Unknown',
            controlNo:  mr.controlNo || mr.mrNo || 'N/A',
            department: mr.department || mr.departmentName || 'N/A',
            gradient:   'from-indigo-600 to-indigo-700',
            Icon:       Package,
            message:    'submitted a new material request.',
          });
        }
      }

      // ── STATUS CHANGED — notify the requestor ──
      if (prev[mr.id] && prev[mr.id].status !== mr.status) {
        if (String(mr.requestedBy) === String(currentUserId)) {
          const s = STATUS_MAP[String(mr.status)];
          if (s) {
            addAlert({
              type:       'status',
              mrId:       mr.id,
              userId:     mr.requestedBy,
              title:      `Request ${s.label}`,
              name:       mr.requestedByName || 'Your request',
              controlNo:  mr.controlNo || mr.mrNo || 'N/A',
              department: mr.department || mr.departmentName || 'N/A',
              gradient:   s.bg,
              Icon:       s.icon,
              message:    s.body,
            });
          }
        }
      }

      // ── CHECKED BY — notify requestor (1 time only) ──
      if (
        prev[mr.id] &&
        !prev[mr.id].checkBy &&
        mr.checkBy &&
        String(mr.requestedBy) === String(currentUserId)
      ) {
        addAlert({
          type:       'checked',
          mrId:       mr.id,
          userId:     mr.checkBy,
          title:      'Request Checked ✓',
          name:       mr.checkByName || 'Staff',
          controlNo:  mr.controlNo || mr.mrNo || 'N/A',
          department: mr.department || mr.departmentName || 'N/A',
          gradient:   'from-teal-500 to-teal-600',
          Icon:       CheckCircle,
          message:    'has checked your material request.',
        });
      }

      // ── UPDATE SNAPSHOT ──
      newSnapshot[mr.id] = {
        status:      mr.status,
        updatedDate: mr.updatedDate,
        checkBy:     mr.checkBy,
      };
    });

    prevDataRef.current    = newSnapshot;
    lastUpdatedRef.current = latestDate;
  }, [currentUserId, addAlert]);

  useNotificationPolling(`${API_BASE_URL}api/MRHeader`, currentUserId, async (arr) => {
    await processData(arr, prevDataRef.current === null);
  });

  const dismiss = (uid) => setAlerts(prev => prev.filter(a => a.uid !== uid));

  // ── If notice_view is off or no alerts, render nothing ──
  if (!isViewOn(notifSettings.notice_view) || alerts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 items-end pointer-events-none">
      {alerts.map((alert, idx) => {
        const Icon       = alert.Icon;
        const profilePic = profileCache[alert.userId];
        return (
          <div
            key={alert.uid}
            className="w-[340px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden pointer-events-auto"
            style={{ animation: 'slideUp 0.3s ease-out both', animationDelay: `${idx * 60}ms` }}
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${alert.gradient} px-4 py-2.5 flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-white text-xs font-bold uppercase tracking-wide">
                  {alert.title}
                </span>
              </div>
              <button
                onClick={() => dismiss(alert.uid)}
                className="p-1 bg-white/10 hover:bg-white/25 rounded-lg transition-all"
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>

            {/* Body */}
            <div className="px-4 py-3.5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {profilePic ? (
                    <img
                      src={profilePic}
                      alt="profile"
                      className="w-10 h-10 rounded-xl object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-500" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="font-bold text-gray-900 text-sm truncate">{alert.name}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1.5">{alert.message}</p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3 text-indigo-400" />
                      <span className="font-mono font-bold text-indigo-700 text-xs">{alert.controlNo}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3 h-3 text-gray-300" />
                      <span className="text-gray-400 text-xs truncate">{alert.department}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-gray-100">
              <div
                className={`h-full bg-gradient-to-r ${alert.gradient} rounded-full`}
                style={{ animation: 'shrink 8s linear forwards' }}
              />
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes shrink {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </div>
  );
}
